import "echarts-gl";

import * as echarts from "echarts";
import { useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

interface IProps {
    bar3Ddata:
        | {
              hours: string[];
              days: string[];
              value: number[][];
          }
        | undefined;
    category?: string;
    isZoomed?: boolean;
}

const useBarChart3DZoom = ({ category, bar3Ddata, isZoomed = false }: IProps) => {
    const { t } = useTranslation();
    const useBar3D = useRef(null);

    const barData = useMemo(() => {
        return bar3Ddata?.value || [[]];
    }, [bar3Ddata?.value]);

    const xData = useMemo(() => {
        return bar3Ddata?.hours;
    }, [bar3Ddata?.hours]);

    const yData = useMemo(() => {
        return bar3Ddata?.days;
    }, [bar3Ddata?.days]);

    const maxValue = useMemo(() => {
        return Math.max(...(barData?.map((item) => item[item.length - 1]) || [60]));
    }, [barData]);

    useLayoutEffect(() => {
        if (!bar3Ddata) return;

        // 等待 DOM 元素準備好
        const initChart = () => {
            if (!useBar3D.current) {
                setTimeout(initChart, 10);
                return;
            }

            let barChart: echarts.ECharts | null = null;

            try {
                barChart = echarts.init(useBar3D.current);
            } catch (error) {
                return;
            }

            if (!barChart) return;
            const option = {
                tooltip: {
                    trigger: "item",
                    textStyle: {
                        fontSize: isZoomed ? 16 : 14,
                    },
                    formatter: function (params: { value: number[] }) {
                        const dayName = yData?.[params.value[1]];
                        const hourName = xData?.[params.value[0]];
                        return (
                            dayName +
                            "<br/>" +
                            hourName +
                            " (UTC+0) <br/>" +
                            t("ANALYSIS.Number_Kiosk_HumanCount") +
                            " : " +
                            params.value[2]
                        );
                    },
                },
                title: {
                    text: category,
                    left: "center",
                    top: "5%",
                    textStyle: {
                        fontSize: isZoomed ? 24 : 16,
                        fontWeight: "bold",
                    },
                },
                visualMap: {
                    max: maxValue || 20,
                    inRange: {
                        color: ["#012ABD", "#05DF69", "#E1D90E", "#F98E37", "#CB1D19"],
                    },
                    textStyle: {
                        fontSize: isZoomed ? 14 : 12,
                    },
                },
                xAxis3D: {
                    type: "category",
                    data: xData,
                    axisLabel: {
                        fontSize: isZoomed ? 14 : 12,
                    },
                },
                yAxis3D: {
                    type: "category",
                    data: yData,
                    axisLabel: {
                        fontSize: isZoomed ? 14 : 12,
                    },
                },
                zAxis3D: {
                    type: "value",
                    axisLabel: {
                        fontSize: isZoomed ? 14 : 12,
                    },
                },
                grid3D: {
                    boxWidth: isZoomed ? 300 : 200,
                    boxDepth: isZoomed ? 120 : 80,
                    viewControl: {
                        projection: "orthographic",
                        autoRotate: isZoomed ? false : undefined,
                        distance: isZoomed ? 200 : 150,
                    },
                    light: {
                        main: {
                            intensity: 1.1,
                            shadow: true,
                        },
                        ambient: {
                            intensity: 0.4,
                        },
                    },
                },
                series: [
                    {
                        name: category,
                        type: "bar3D",
                        data: barData.map(function (item) {
                            return {
                                value: [item[1], item[0], item[2]],
                            };
                        }),
                        shading: "lambert",
                        label: {
                            fontSize: isZoomed ? 16 : 14,
                            borderWidth: 2,
                            show: isZoomed,
                        },
                        itemStyle: {
                            opacity: isZoomed ? 0.8 : 0.7,
                        },
                        emphasis: {
                            label: {
                                fontSize: isZoomed ? 36 : 32,
                                color: "#090e12",
                            },
                            itemStyle: {
                                opacity: 1,
                                color: "#3D5668",
                            },
                        },
                    },
                ],
            };
            try {
                barChart.setOption(option);
            } catch (error) {
                return;
            }

            // 響應式調整
            const handleResize = () => {
                if (barChart && !barChart.isDisposed()) {
                    try {
                        barChart.resize();
                    } catch (error) {
                        // 靜默處理錯誤
                    }
                }
            };

            window.addEventListener("resize", handleResize);

            return () => {
                window.removeEventListener("resize", handleResize);
                if (barChart && !barChart.isDisposed()) {
                    try {
                        barChart.dispose();
                    } catch (error) {
                        // 靜默處理錯誤
                    }
                }
            };
        };

        initChart();
    }, [category, barData, xData, yData, maxValue, isZoomed, t, bar3Ddata]);

    return useBar3D;
};

export default useBarChart3DZoom;
